import React, { type FC, type ReactNode } from "react";

import { OmegaUiProvider } from "../omega-ui";
import { OverlayProvider } from "../overlay";
import { ThemeProvider } from "../styles/themes";
import { NotificationProvider } from "./NotificationProvider";
import { AppErrorBoundary } from "./ErrorBoundary";
import { SessionEventsProvider } from "../services/session";
import { QueryProvider } from "./QueryProvider";
import { ScannerOverlaySuppressBridge, ScannerProvider } from "../scanner";

interface AppProvidersProps {
    children: ReactNode;
}

/**
 * Корневые провайдеры: theme → session → query → **scanner → overlay → omega-ui** → notification → error boundary.
 *
 * ## Почему `ScannerProvider` снаружи `OverlayProvider`
 *
 * React Context распространяется по дереву **компонентов**, а не по DOM.
 * `OverlayHost` (внутри `OverlayProvider`) рендерит window/dialog через portal в `body`,
 * но в React-дереве content остаётся потомком `OverlayProvider`.
 *
 * Если поставить `ScannerProvider` **внутрь** `OverlayProvider`, получится:
 *
 * ```text
 * OverlayProvider
 *   FloatingLayerProvider
 *     ScannerProvider        ← ScannerContext только здесь (экран + hint)
 *     OverlayHost → окна     ← сосед, без ScannerContext → краш useScannerRegistration
 * ```
 *
 * Нужно наоборот — весь `OverlayProvider` (включая `OverlayHost`) внутри `ScannerProvider`:
 *
 * ```text
 * ScannerProvider
 *   OverlayProvider
 *     OmegaUiProvider        ← picker fullscreen shell использует ViewportLayer
 *     ScannerOverlaySuppressBridge
 *     app routes
 *     OverlayHost → окна     ← потомок ScannerProvider ✓
 *   ScannerHintOverlay       ← потомок ScannerProvider ✓
 * ```
 *
 * ## Что **не** решается порядком провайдеров
 *
 * Z-index `ScannerHintOverlay` — через portal в `body` и токен `--z-scannerHint` в теме,
 * а не через инверсию `ScannerProvider` / `OverlayProvider`.
 *
 * ## Router context
 *
 * `BrowserRouter` должен оборачивать `AppProviders` (см. `AppRouter`): `OverlayHost`
 * рендерится рядом с `children` внутри `OverlayProvider`, и ему нужен Router context
 * для `useNavigate` / `useLocation` в overlay-окнах.
 *
 * ## `ScannerOverlaySuppressBridge`
 *
 * Первый ребёнок `OverlayProvider`: ему нужны оба context (overlay stack + scanner suppress).
 * Не монтировать внутри одного только `ScannerProvider` — overlay context будет недоступен.
 *
 * ## `OmegaUiProvider`
 *
 * Внутри `OverlayProvider`, чтобы `renderPickerFullscreen` мог использовать `ViewportLayer`
 * (portal + z-index + back handler из floating-layer context).
 */

export const AppProviders: FC<AppProvidersProps> = ({ children }) => (
    <ThemeProvider>
        <SessionEventsProvider>
            <QueryProvider>
                <ScannerProvider>
                    <OverlayProvider>
                        <OmegaUiProvider>
                            <ScannerOverlaySuppressBridge />
                            <NotificationProvider>
                                <AppErrorBoundary>{children}</AppErrorBoundary>
                            </NotificationProvider>
                        </OmegaUiProvider>
                    </OverlayProvider>
                </ScannerProvider>
            </QueryProvider>
        </SessionEventsProvider>
    </ThemeProvider>
);
